import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import {
   faArrowAltFromLeft,
   faBomb,
   faCassetteTape,
   faExternalLink,
   faMicrophoneSlash,
   faParking,
   faPauseCircle,
   faPhone,
   faPhoneSlash,
   faProjectDiagram,
   faQuestionCircle,
   faRandom,
   faRepeat1,
   faRetweetAlt,
   faTag,
   faUser,
   faUsers,
} from '@fortawesome/pro-duotone-svg-icons';
import { Store } from '@ngxs/store';
import _ from 'lodash';
import { BehaviorSubject, Observable, combineLatest, iif, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { WellKnownIconsEnum } from 'src/app/helper/iconHelper';
import { EmailPurpose } from 'src/app/samwebDescription/samwin/EmailPurpose';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { QueueSelectionPurpose } from 'src/app/samwebDescription/samwin/QueueSelectionPurpose';
import { UpdateCallTag } from 'src/app/store/samwin/call-tags/call-tags.actions';
import { CallTagsSelectors } from 'src/app/store/samwin/call-tags/call-tags.selectors';
import {
   AcceptRingingCallAction,
   BargeInConference,
   BargeInListen,
   BargeInWhisper,
   DisconnectLineA,
   DisconnectLineB,
   EndCallAction,
   MarkMaliciousCall,
   ParkCallAgent,
   ParkCallSkill,
   ReQueueCall,
   ReassignCall,
   StartConference,
   StartNewCallAction,
   SwitchHoldLines,
   ToggleHoldLineA,
   ToggleHoldLineB,
   ToggleManualCallRecording,
} from 'src/app/store/samwin/client-phone/client-phone.actions';
import { ClientPhoneState } from 'src/app/store/samwin/client-phone/client-phone.state';
import { StartEmailProcess } from 'src/app/store/samwin/email/email-dialog/email-dialog-behaviour.actions';
import { NextDialTargetState } from 'src/app/store/samwin/next-dial-target/next-dial-target.state';
import { OpenDynamicLinkState } from 'src/app/store/samwin/open-dynamic-link/open-dynamic-link.state';
import { StartQueueSelectionProcess } from 'src/app/store/samwin/queue-selection-dialog/queue-selection-dialog.actions';
import { SamwinCallListState } from 'src/app/store/samwin/samwin-call-list/samwin-call-list.state';
import { SamwinLinesStateSelector } from 'src/app/store/samwin/samwin-lines/samwin-lines.selector';
import { SamwinSkillsSelectors } from 'src/app/store/samwin/samwin-skills/samwin-skills.selectors';
import { SamwinSkillsState } from 'src/app/store/samwin/samwin-skills/samwin-skills.state';
import { SkillConstants } from 'src/app/store/samwin/samwin-skills/skill-constants';
import { SamwinUserAssistanceState } from 'src/app/store/samwin/samwin-user-assistance-status/samwin-user-assistance-status.state';
import { RequestUserStatusChange } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.actions';
import { SamwinUserStatusSelectors } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.selectors';
import { SamwinUserStatusState } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.state';
import { OpenUrlInNewWindow } from 'src/app/store/samwin/samwin.actions';
import { TransferCallAction } from 'src/app/store/samwin/transfer/transfer.actions';
import { TransferState } from 'src/app/store/samwin/transfer/transfer.state';
import { EnabledFeaturesState } from 'src/app/store/session/enabled-features/enabled-features.state';
import { ToggleMuteMicrophoneAction } from 'src/app/store/settings/audio-settings/audio-settings.actions';
import { AudioSettingsState } from 'src/app/store/settings/audio-settings/audio-settings.state';
import { MakeCallActionSettingRemoteDto } from 'src/generated/signalR/make-call-action-setting-remote-dto';
import { OpenDynamicLinkActionSettingRemoteDto } from 'src/generated/signalR/open-dynamic-link-action-setting-remote-dto';
import { Originator } from 'src/generated/signalR/originator';
import { ReQueueActionSettingRemoteDto } from 'src/generated/signalR/re-queue-action-setting-remote-dto';
import { RequestUserStatusActionSettingRemoteDto } from 'src/generated/signalR/request-user-status-action-setting-remote-dto';
import { SamwinCallInfoRemoteDto } from 'src/generated/signalR/samwin-call-info-remote-dto';
import { SetTagActionSettingRemoteDto } from 'src/generated/signalR/set-tag-action-setting-remote-dto';
import { UserActionSettingBaseRemoteDto } from 'src/generated/signalR/user-action-setting-base-remote-dto';
import { Enum, EnumType } from 'ts-jenum';

@Enum<UserActionTyp>('value')
export class UserActionTyp extends EnumType<UserActionTyp>() {
   static readonly ACCEPT_CALL = new UserActionTyp(
      'ACCEPT_CALL',
      (store: Store) => of('user_actions.types.accept_call.title'),
      'user_actions.types.accept_call.description',
      faPhone,
      (store) => store.dispatch(new AcceptRingingCallAction()),
      (store) => store.select(ClientPhoneState.isCallRingingAndAcceptable),
      null,
      (store: Store) => of(true)
   );

   static readonly END_CALL = new UserActionTyp(
      'END_CALL',
      (store: Store) => of('user_actions.types.end_call.title'),
      'user_actions.types.end_call.description',
      faPhoneSlash,
      (store) => store.dispatch(new EndCallAction()),
      (store) => store.select(SamwinLinesStateSelector.canEndCall),
      null,
      (store: Store) => of(true)
   );

   static readonly DISCONNECT_LINE_A = new UserActionTyp(
      'DISCONNECT_LINE_A',
      (store: Store) => of('user_actions.types.disconnect_line_a.title'),
      'user_actions.types.disconnect_line_a.description',
      faPhoneSlash,
      (store) => store.dispatch(new DisconnectLineA()),
      (store) => store.select(SamwinLinesStateSelector.canDisconnectLineA),
      null,
      (store: Store) => of(true)
   );

   static readonly DISCONNECT_LINE_B = new UserActionTyp(
      'DISCONNECT_LINE_B',
      (store: Store) => of('user_actions.types.disconnect_line_b.title'),
      'user_actions.types.disconnect_line_b.description',
      faPhoneSlash,
      (store) => store.dispatch(new DisconnectLineB()),
      (store) => store.select(SamwinLinesStateSelector.canDisconnectLineB),
      null,
      (store: Store) => of(true)
   );

   static readonly TOGGLE_HOLD_LINE_A = new UserActionTyp(
      'TOGGLE_HOLD_LINE_A',
      (store: Store) => of('user_actions.types.toggle_hold_line_a.title'),
      'user_actions.types.toggle_hold_line_a.description',
      faPauseCircle,
      (store) => store.dispatch(new ToggleHoldLineA()),
      (store) => store.select(SamwinLinesStateSelector.canToggleHoldLineA),
      null,
      (store: Store) => of(true),
      (store) => store.select(SamwinLinesStateSelector.isLineAOnHold)
   );

   static readonly TOGGLE_HOLD_LINE_B = new UserActionTyp(
      'TOGGLE_HOLD_LINE_B',
      (store: Store) => of('user_actions.types.toggle_hold_line_b.title'),
      'user_actions.types.toggle_hold_line_b.description',
      faPauseCircle,
      (store) => store.dispatch(new ToggleHoldLineB()),
      (store) => store.select(SamwinLinesStateSelector.canToggleHoldLineB),
      null,
      (store: Store) => of(true),
      (store) => store.select(SamwinLinesStateSelector.isLineBOnHold)
   );

   static readonly TOGGLE_MANUAL_CALL_RECORDING = new UserActionTyp(
      'TOGGLE_MANUAL_CALL_RECORDING',
      (store: Store) => {
         return store.select(SamwinLinesStateSelector.isCallBeingRecorded).pipe(
            map((isCallBeingRecorded) => {
               if (isCallBeingRecorded) {
                  return 'user_actions.types.manual_call_recording.title.stop';
               } else {
                  return 'user_actions.types.manual_call_recording.title.start';
               }
            })
         );
      },
      'user_actions.types.manual_call_recording.description',
      faCassetteTape,
      (store: Store) => store.dispatch(new ToggleManualCallRecording()),
      (store: Store) => store.select(SamwinLinesStateSelector.canToggleManualCallRecording),
      null,
      (store: Store) => store.select(EnabledFeaturesState.allowedToManuallyRecord),
      (store: Store) => store.select(SamwinLinesStateSelector.isCallBeingRecorded)
   );

   static readonly MARK_MALICIOUS_CALL = new UserActionTyp(
      'MARK_MALICIOUS_CALL',
      (store: Store) => of('user_actions.types.mark_malicious_call.title'),
      'user_actions.types.mark_malicious_call.description',
      faBomb,
      (store: Store) => store.dispatch(new MarkMaliciousCall(store.selectSnapshot(SamwinLinesStateSelector.getCurrentCallShortInfo).callId)),
      (store: Store) => store.select(SamwinLinesStateSelector.canMarkMaliciousCall),
      null,
      (store: Store) => of(true),
      (store: Store) => store.select(SamwinLinesStateSelector.isCallMarkedAsMalicious)
   );

   static readonly SWITCH_HOLD_LINES = new UserActionTyp(
      'SWITCH_HOLD_LINES',
      (store: Store) => of('user_actions.types.switch_hold_lines.title'),
      'user_actions.types.switch_hold_lines.description',
      faRandom,
      (store) => store.dispatch(new SwitchHoldLines()),
      (store: Store) => store.select(SamwinLinesStateSelector.canSwitchHoldLines),
      null,
      (store: Store) => of(true)
   );

   static readonly REASSIGN_CALL = new UserActionTyp(
      'REASSIGN_CALL',
      (store: Store) => of('user_actions.types.reassign_call.title'),
      'user_actions.types.reassign_call.description',
      faRepeat1,
      (store) => store.dispatch(new ReassignCall()),
      (store) => store.select(SamwinLinesStateSelector.isPhoneRinging),
      null,
      (store: Store) => of(true)
   );

   static readonly PARK_CALL_PRIVATE = new UserActionTyp(
      'PARK_CALL_PRIVATE',
      (store: Store) => of('user_actions.types.park_call_private.title'),
      'user_actions.types.park_call_private.description',
      faParking,
      (store) => store.dispatch(new ParkCallAgent()),
      (store) => store.select(SamwinLinesStateSelector.canCallBeParkedAtAgent),
      null,
      (store: Store) => of(true)
   );

   static readonly PARK_CALL_SKILL = new UserActionTyp(
      'PARK_CALL_SKILL',
      (store: Store) => of('user_actions.types.park_call_skill.title'),
      'user_actions.types.park_call_skill.description',
      faParking,
      (store) => store.dispatch(new ParkCallSkill()),
      (store) => store.select(SamwinLinesStateSelector.canCallBeParkedInSkill),
      null,
      (store: Store) => of(true)
   );

   static readonly REQUEUE_CALL = new UserActionTyp(
      'REQUEUE_CALL',
      (store: Store) => of('user_actions.types.requeue_call.title'),
      'user_actions.types.requeue_call.description',
      faRetweetAlt,
      (store, actionSettings: ReQueueActionSettingRemoteDto) => {
         const call = store.selectSnapshot(SamwinCallListState.getCallByPositionFn)(actionSettings.callPosition);
         if (actionSettings.skillId === SkillConstants.OPEN_REQUEUE_DIALOG) {
            return store.dispatch(new StartQueueSelectionProcess(QueueSelectionPurpose.REQUEUE, call, call?.queueId));
         }
         return store.dispatch(new ReQueueCall(call.callId, actionSettings.skillId));
      },
      (store, actionSettings: ReQueueActionSettingRemoteDto) =>
         _.isNil(actionSettings)
            ? of(false)
            : store.select(SamwinCallListState.getCallByPositionFn).pipe(
                 map((fn) => fn(actionSettings.callPosition)),
                 distinctUntilChanged(),
                 switchMap((call: SamwinCallInfoRemoteDto) =>
                    iif(() => !_.isNil(call) && call?.queueId !== actionSettings.skillId, store.select(SamwinLinesStateSelector.canCallBeReQueued), of(false))
                 )
              ),
      <ReQueueActionSettingRemoteDto>{
         callPosition: 1,
         skillId: SkillConstants.OPEN_REQUEUE_DIALOG,
         actionSettingTypeIdentifier: 'ReQueueActionSettingRemoteDto',
      },
      (store: Store) => of(true)
   );

   static readonly EXECUTE_IVR = new UserActionTyp(
      'EXECUTE_IVR',
      (store: Store) => of('user_actions.types.execute_ivr.title'),
      'user_actions.types.execute_ivr.description',
      faProjectDiagram,
      (store) =>
         store.dispatch(
            new StartQueueSelectionProcess(
               QueueSelectionPurpose.EXECUTE_IVR,
               store.selectSnapshot(SamwinCallListState.getCallByIdFn)(store.selectSnapshot(SamwinLinesStateSelector.getCurrentCallShortInfo).callId)
            )
         ),
      (store) => store.select(SamwinLinesStateSelector.canExecuteIvr),
      null,
      (store: Store) => of(true)
   );

   static readonly TOGGLE_MUTE_MICROPHONE = new UserActionTyp(
      'TOGGLE_MUTE_MICROPHONE',
      (store: Store) => of('user_actions.types.toggle_mute_microphone.title'),
      'user_actions.types.toggle_mute_microphone.description',
      faMicrophoneSlash,
      (store) => store.dispatch(new ToggleMuteMicrophoneAction()),
      (store) => of(true),
      null,
      (store: Store) => of(true),
      (store: Store) => store.select(AudioSettingsState.isMicrophoneMuted)
   );

   static readonly TRANSFER_CALL = new UserActionTyp(
      'TRANSFER_CALL',
      (store: Store) => of('user_actions.types.transfer_call.title'),
      'user_actions.types.transfer_call.description',
      faArrowAltFromLeft,
      (store) => store.dispatch(new TransferCallAction()),
      (store) => store.select(TransferState.canTransfer),
      null,
      (store: Store) => of(true)
   );

   static readonly REQUEST_USER_STATUS = new UserActionTyp(
      'REQUEST_USER_STATUS',
      (store: Store) => of('user_actions.types.request_user_status.title'),
      'user_actions.types.request_user_status.description',
      faUser,
      (store, actionSettings: RequestUserStatusActionSettingRemoteDto) => {
         return store.dispatch(new RequestUserStatusChange(actionSettings.requestUserStatus, Originator.User));
      },
      (store, actionSettings: RequestUserStatusActionSettingRemoteDto) =>
         _.isNil(actionSettings)
            ? of(false)
            : store.select(SamwinUserStatusSelectors.getAllowedFollowUpState).pipe(map((fn) => fn(actionSettings.requestUserStatus))),
      <RequestUserStatusActionSettingRemoteDto>{
         requestUserStatus: -1,
         actionSettingTypeIdentifier: 'RequestUserStatusActionSettingRemoteDto',
      },
      (store: Store) => of(true),
      (store: Store, actionSettings: RequestUserStatusActionSettingRemoteDto) =>
         store.select(SamwinUserStatusState.getCurrentStatusId).pipe(map((statusId) => statusId === actionSettings?.requestUserStatus))
   );

   static readonly OPEN_DYNAMIC_LINK = new UserActionTyp(
      'OPEN_DYNAMIC_LINK',
      (store: Store) => of('user_actions.types.open_dynamic_link.title'),
      'user_actions.types.open_dynamic_link.description',
      faExternalLink,
      (store: Store, actionSettings: OpenDynamicLinkActionSettingRemoteDto) => store.dispatch(new OpenUrlInNewWindow(actionSettings.url)),
      (store: Store, actionSettings: OpenDynamicLinkActionSettingRemoteDto) =>
         _.isNil(actionSettings) ? of(false) : store.select(OpenDynamicLinkState.canOpenDynamicLinkFn).pipe(map((fn) => fn(actionSettings.url))),
      <OpenDynamicLinkActionSettingRemoteDto>{
         url: '',
         actionSettingTypeIdentifier: 'OpenDynamicLinkActionSettingRemoteDto',
      },
      (store: Store) => of(true),
      (store: Store) => of(false),
      (store: Store, actionSettings: OpenDynamicLinkActionSettingRemoteDto) => OpenDynamicLinkState.emitOnAutoExecuteTrigger$(store, actionSettings)
   );

   static readonly START_CONFERENCE = new UserActionTyp(
      'START_CONFERENCE',
      (store: Store) => of('user_actions.types.conference.title'),
      'user_actions.types.conference.description',
      faUsers,
      (store: Store) => store.dispatch(new StartConference()),
      (store: Store) => store.select(SamwinLinesStateSelector.canStartConference),
      null,
      (store: Store) => of(true),
      (store: Store) => store.select(SamwinLinesStateSelector.isInConference)
   );

   static readonly MAKE_CALL = new UserActionTyp(
      'MAKE_CALL',
      (store: Store) => of('user_actions.types.make_call.title'),
      'user_actions.types.make_call.description',
      faPhone,
      (store: Store, actionSettings: MakeCallActionSettingRemoteDto) => {
         // fetch the" context number" (we use the dial pad for now, make call from directory will not work)
         return store.dispatch(new StartNewCallAction(store.selectSnapshot(NextDialTargetState.nextDialTarget), actionSettings.skillId));
      },
      (store: Store) => store.select(ClientPhoneState.canMakeCall),
      <MakeCallActionSettingRemoteDto>{
         skillId: 0,
         actionSettingTypeIdentifier: 'MakeCallActionSettingRemoteDto',
      },
      (store: Store) => of(true)
   );

   static readonly SET_TAG = new UserActionTyp(
      'SET_TAG',
      (store: Store) => of('user_actions.types.set_tag.title'),
      'user_actions.types.set_tag.description',
      faTag,
      (store: Store, actionSettings: SetTagActionSettingRemoteDto) => {
         const callId = store.selectSnapshot(CallTagsSelectors.lastActiveCallIdWithTag);
         const isCallTagSetToValue = store.selectSnapshot(CallTagsSelectors.isCallTagSetToValueOnActiveOrRingingCall);
         if (isCallTagSetToValue(actionSettings.tagName, actionSettings.tagValue)) {
            return store.dispatch(new UpdateCallTag(callId, actionSettings.tagName, ''));
         } else {
            return store.dispatch(new UpdateCallTag(callId, actionSettings.tagName, actionSettings.tagValue));
         }
      },
      (store: Store, actionSettings: SetTagActionSettingRemoteDto) => {
         if (_.isNil(actionSettings) || _.isNil(actionSettings.tagName) || _.isEmpty(actionSettings.tagName)) {
            return of(false);
         }
         return store.select(CallTagsSelectors.areCallTagsEditable);
      },
      <SetTagActionSettingRemoteDto>{
         tagName: '',
         tagValue: '',
         actionSettingTypeIdentifier: 'SetTagActionSettingRemoteDto',
      },
      (store: Store) => of(true),
      (store: Store, actionSettings: SetTagActionSettingRemoteDto) => {
         if (_.isNil(actionSettings)) {
            return of(false);
         }
         return store
            .select(CallTagsSelectors.isCallTagSetToValueOnActiveOrRingingCall)
            .pipe(switchMap((fn) => of(fn(actionSettings.tagName, actionSettings.tagValue))));
      }
   );

   static readonly COMPOSE_EMAIL = new UserActionTyp(
      'COMPOSE_EMAIL',
      (store: Store) => of('user_actions.types.email_dialog.title'),
      'user_actions.types.email_dialog.description',
      WellKnownIconsEnum.EMAILING_COMPOSE.icon,
      (store, actions) => store.dispatch(new StartEmailProcess(EmailPurpose.COMPOSE_EMAIL)),
      (store) => {
         const isEmailingFeatureEnabled$ = store.select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.SAMWIN_7_SOCIAL_MEDIA));
         const isEmailAndTextingFeatureEnabled$ = store.select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.USE_EMAIL_AND_TEXTING));
         const currentSkill$ = store.select(SamwinSkillsSelectors.getCurrentAcdOrCallSkillConfiguration);
         const hasConnector$ = store.select(SamwinSkillsState.hasCurrentUserEmailConnector);
         return combineLatest([isEmailingFeatureEnabled$, isEmailAndTextingFeatureEnabled$, currentSkill$, hasConnector$]).pipe(
            map(
               ([isEmailingEnabled, isEmailAndTextingEnabled, currentSkill, hasConnector]) =>
                  (isEmailingEnabled || isEmailAndTextingEnabled) && hasConnector(currentSkill?.id ?? SkillConstants.OVERALL_SKILL_ID)
            )
         );
      },
      null,
      (store: Store) => {
         const isEmailingFeatureEnabled$ = store.select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.SAMWIN_7_SOCIAL_MEDIA));
         const isEmailAndTextingFeatureEnabled$ = store.select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.USE_EMAIL_AND_TEXTING));
         return combineLatest([isEmailingFeatureEnabled$, isEmailAndTextingFeatureEnabled$]).pipe(
            map(([isEmailingEnabled, isEmailAndTextingEnabled]) => isEmailingEnabled || isEmailAndTextingEnabled)
         );
      }
   );

   static readonly BARGE_IN_LISTEN = new UserActionTyp(
      'BARGE_IN_LISTEN',
      (store: Store) => of('user_actions.types.barge_in_listen.title'),
      'user_actions.types.barge_in_listen.description',
      WellKnownIconsEnum.BARGE_IN_LISTEN.icon,
      (store, actionSettings) => store.dispatch(new BargeInListen()),
      (store) => store.select(SamwinUserAssistanceState.canBargeIn),
      null,
      (store) => of(true),
      (store: Store) => store.select(SamwinUserAssistanceState.isListening)
   );

   static readonly BARGE_IN_WHISPER = new UserActionTyp(
      'BARGE_IN_WHISPER',
      (store: Store) => of('user_actions.types.barge_in_whisper.title'),
      'user_actions.types.barge_in_whisper.description',
      WellKnownIconsEnum.BARGE_IN_WHISPER.icon,
      (store, actionSettings) => store.dispatch(new BargeInWhisper()),
      (store) => store.select(SamwinUserAssistanceState.canBargeIn),
      null,
      (store) => of(true),
      (store: Store) => store.select(SamwinUserAssistanceState.isWhispering)
   );

   static readonly BARGE_IN_CONFERENCE = new UserActionTyp(
      'BARGE_IN_CONFERENCE',
      (store: Store) => of('user_actions.types.barge_in_conference.title'),
      'user_actions.types.barge_in_conference.description',
      WellKnownIconsEnum.BARGE_IN_CONFERENCE.icon,
      (store, actionSettings) => store.dispatch(new BargeInConference()),
      (store) => store.select(SamwinUserAssistanceState.canBargeIn),
      null,
      (store) => of(true),
      (store: Store) => store.select(SamwinUserAssistanceState.isConferencing)
   );

   static readonly UNKNOWN_ACTION = new UserActionTyp(
      'UNKNOWN_ACTION',
      (store: Store) => of('user_actions.types.unknown.title'),
      '',
      faQuestionCircle,
      (store) => {
         return of(false);
      },
      (store) => of(false),
      null,
      (store: Store) => of(true)
   );

   public readonly isExecuting$ = new BehaviorSubject(false);

   constructor(
      public value,
      public getTranslationKey$: (store: Store) => Observable<string>,
      public descriptionTextKey,
      public defaultIcon: IconDefinition,
      private executionAction$: (store: Store, actionSettings: UserActionSettingBaseRemoteDto) => Observable<any>,
      public isActionExecutable$: (store: Store, actionSettings: UserActionSettingBaseRemoteDto) => Observable<boolean>,
      public defaultActionSettings: UserActionSettingBaseRemoteDto,
      public allowedForUserSelection$: (store: Store) => Observable<boolean>,
      public isActionToggled$?: (store: Store, actionSettings: UserActionSettingBaseRemoteDto) => Observable<boolean>,
      public autoExecuteOnEmit$?: (store: Store, actionSettings: UserActionSettingBaseRemoteDto) => Observable<any>
   ) {
      super();
   }

   public static selectableActions$(store: Store): Observable<UserActionTyp[]> {
      const allActionTypes = UserActionTyp.values();
      return combineLatest(
         allActionTypes.map((actionType) =>
            actionType.allowedForUserSelection$(store).pipe(
               map((isAllowed) => {
                  return {
                     type: actionType,
                     isAllowed: isAllowed,
                  };
               })
            )
         )
      ).pipe(
         map((actionTypesMapped) => {
            return _(actionTypesMapped)
               .filter((x) => x.isAllowed)
               .map((x) => x.type)
               .value();
         })
      );
   }

   public executeUserAction$(store: Store, actionSettings: UserActionSettingBaseRemoteDto): Observable<any> {
      return of(null)
         .pipe(switchMap(() => this.isActionExecutable$(store, actionSettings)))
         .pipe(
            filter((isActionExecutable) => isActionExecutable),
            take(1)
         )
         .pipe(tap(() => this.isExecuting$.next(true)))
         .pipe(switchMap(() => this.executionAction$(store, actionSettings)))
         .pipe(tap(() => this.isExecuting$.next(false)));
   }
}
